"use client";

import { useEffect, useState, type ReactElement } from "react";

interface VisitedProps {
  VisitedComponent: ReactElement;
  pageId: string;
  pageType: string;
  writeCookie?: boolean;
}

// Used to work with cookies, but refactored to use localStorage, hence the naming
const handleSetCookie = async (name, value) => {
  if (typeof window !== "undefined") {
    localStorage.setItem(name, JSON.stringify(value));
  }
};
const handleGetCookie = async (name) => {
  let value = localStorage.getItem(name)
    ? JSON.parse(localStorage.getItem(name) ?? "")
    : [];

  if (!Array.isArray(value)) {
    value = [];
  }

  return value;
};

/**
 * Component to show a different component based on whether the user has visited the page before.
 *
 * It automatically writes a cookie to the user's browser to keep track of which pages they've visited,
 * unless 'writeCookie' is set to false. E.g. when you want to show the 'visited' state in an overview.
 * This means that unless 'writeCookie' is set to 'false', the state will be 'visited' on the next visit.
 */

const Visited = ({
  pageType,
  pageId,
  VisitedComponent,
  writeCookie = true,
}: VisitedProps) => {
  const [visitedBefore, setVisitedBefore] = useState(null);

  useEffect(() => {
    const cookieName = `visited-${pageType}-ids`;

    if (visitedBefore === null) {
      handleGetCookie(cookieName).then((cookieValue) => {
        const visitedPageIds = cookieValue ?? [];
        const hit = visitedPageIds.includes(pageId);
        setVisitedBefore(hit);

        if (!hit && writeCookie) {
          visitedPageIds.push(pageId);
          void handleSetCookie(cookieName, visitedPageIds);
        }
      });
    }
  }, []);

  // Show nothing while parsing
  if (visitedBefore === null) {
    return null;
  }

  return visitedBefore ? VisitedComponent : <></>;
};
export default Visited;
